import { ServiceBase } from "../ServiceBase";
import { ICrmService } from "./crm.service.interface";
import { IServiceFactoryExtended } from "../service-factory-extended.interface";
import {ICrmSdkLib, ReasonIsNotEligible, CheckEligibilityResponse} from "../../../common/interfaces/crm-sdk-lib.interface";

export class CrmService extends ServiceBase implements ICrmService {
    constructor(serviceFactory: IServiceFactoryExtended) {
        super(serviceFactory);
        this._initService();
    }


    private _initService() {
        if (!this.isEnabled) {
            return;
        }

        this.services.sdk.events.onFirebaseGotNewToken((eventData) => {
            this._connect(eventData.token);
        });

        this.reaction(() => this.services.user.isAuthorized, (isAuth) => {
            if (isAuth) {
                this._initCrm();
            } else {
                this._disconnect();
            }
        }, {
            fireImmediately: true
        });
    }
  
    get isEnabled(): boolean {
        return this.services.configuration.servicesConfigs.CrmSdk.enabled && 
            !!this.services.configuration.servicesConfigs.CrmSdk.libUrl;
    }

    private _sdkLib: null | ICrmSdkLib = null;
    private _initCrmPromise: Promise<void> | null = null;

    private async _loadLib() {
        if (!this._sdkLib) {
            await this.services.scriptLoader.loadScript({
                url: this.services.configuration.servicesConfigs.CrmSdk.libUrl
            })
            this._sdkLib = this.services.window.getGlobalProperty('platformCrmSdk');
        }
    }

    private async _initCrm() {
        //prevent calling sdkLib.initCrm twice in this session
        if (this._initCrmPromise) {
            return this._initCrmPromise;
        }

        const executeInitCrm = async () => {
            try {
                const platformToken = await this.services.sdk.getPlatformToken();
                if (!platformToken?.token) {
                    throw new Error("Cannot retrieve platform token!");
                }

                await this._loadLib();
                const res = await this._sdkLib.initCrm({
                    userToken: platformToken.token
                });
                this.services.logger.info('crmSdk.initCrm() succeeded', res);
            } catch (err) {
                this.services.logger.error('crmSdk.initCrm() failed', err);
                throw err;
            }
        }

        this._initCrmPromise = executeInitCrm();
        
        this._initCrmPromise.catch(() => {
            // try again next time if something failed
            this._initCrmPromise = null;
        });
        return this._initCrmPromise;
    };

    private async _connect(fbToken: string) {
        try {
            await this._initCrm();
            await this._sdkLib.onConnectedToFirebase({
                fbToken: fbToken
            });
            this.services.logger.info('crmSdk.onConnectedToFirebase() succeeded');
        } catch (err) {
            this.services.logger.error('crmSdk.onConnectedToFirebase() failed', err);
            throw err;
        }
    }

    private async _disconnect() {
        this._initCrmPromise = null;
        if (this._sdkLib) {
            try {
                await this._sdkLib.onDisconnectedFromFirebase();
                this.services.logger.info('crmSdk.onDisconnectedFromFirebase() succeeded');
            } catch (err) {
                this.services.logger.error('crmSdk.onDisconnectedFromFirebase() failed', err);
                throw err;
            }
        }
    }

    async _getEligibility(): Promise<CheckEligibilityResponse> {
        try {
            await this._initCrm();
            const eligibility = await this._sdkLib.checkEligibility();
            this.services.logger.info('crmSdk.checkEligibility() response', eligibility);
            return eligibility;
        } catch(err) {
            this.services.logger.error('crmSdk.checkEligibility() failed', err);
            throw err;
        }
    }

    async isPromotionRestricted(): Promise<boolean> {
        if (!this.services.user.isAuthorized){
            this.services.logger.warning('Promotion safe check - must be a logged in user - failed')
            return false
        }

        const eligibility = await this._getEligibility();
        const doNotShowPromotionReasons = [ReasonIsNotEligible.PromotionRestricted, /*ReasonIsNotEligible.NoSegmentationFound*/];
        // In case user is fully eligible - meaning he did not play today and deposited in last 30 days and not vip
        // they return isEligible = true
        // This condition says if he's fully eligible or he is not fully eligible but reason is different than promotion permitted
        const shouldShowPromotion =  eligibility.isEligible || !doNotShowPromotionReasons.includes(eligibility.reasonIsNotEligible);
        return !shouldShowPromotion;
    }
}
